import { Link } from 'react-router-dom';
import { Container, ContainerSize, Heading, Nav, Tabs, Text } from '@rebootui';
import CodeSnippet from "../../../components/controls/CodeSnippet";
import CodePreview from "../../../components/controls/CodePreview";
import CodeHighlighter from "../../../components/controls/CodeHighlighter";

type NavPageProps = {};

const NavPage = ({ }: NavPageProps) => {
    return (
        <Container size={ContainerSize.Fluid}>
            <Heading el="h1" className="fw-semibold mb-3 ls-1">
                Navs & Tabs
            </Heading>
            <Text el="p" className="mb-4">
                Documentation and examples for how to use Bootstrap's included navigation components. The <Text el="strong">Nav</Text> component provides a flexible navigation system with support for tabs, pills, and various alignments.
            </Text>

            <Heading el="h2" className="fw-medium fs-3 mt-4 mb-3">
                Nav and Navbar
            </Heading>
            <Text el="p" className="mb-3">
                On its own, <Text el="code">Nav</Text> renders a <Text el="code">ul</Text> with the <Text el="code">nav</Text> class. When it is nested inside <Text el="code">Navbar</Text>, it automatically uses <Text el="code">navbar-nav</Text> instead, matching Bootstrap’s markup.
            </Text>
            <Text el="p" className="mb-3">
                <Text el="code">Nav.Link</Text> renders a plain <Text el="code">{"<a>"}</Text> by default (use <Text el="code">href</Text> or <Text el="code">to</Text> for the URL). For client-side routing, pass <Text el="code">as=&#123;Link&#125;</Text> from <Text el="code">react-router-dom</Text> (or any compatible component that accepts <Text el="code">to</Text> and <Text el="code">className</Text>) so you keep Bootstrap’s nav styling without full page reloads. The library does not bundle React Router.
            </Text>
            <Text el="p" className="mb-4">
                See the <Link to="/docs/component/navbar" className="link-primary">Navbar</Link> page for a full responsive header example with <Text el="code">Nav</Text>.
            </Text>

            <Heading el="h2" className="fw-medium fs-3 mt-4 mb-3">
                Class names
            </Heading>
            <Text el="p" className="mb-4">
                The base <Text el="code">nav</Text> class is applied for you. You can add Bootstrap variants and utilities on <Text el="code">Nav</Text> (for example <Text el="code">nav-tabs</Text>, <Text el="code">justify-content-center</Text>) without repeating <Text el="code">nav</Text> in <Text el="code">className</Text>, though including it matches the Bootstrap docs and is harmless.
            </Text>

            {/* Base Nav */}
            <Heading el="h2" className="fw-medium fs-3 mt-4 mb-3">
                Base nav
            </Heading>
            <Text className="mb-4">
                Navigation available in Bootstrap share general markup and styles, from the base <Text el="strong">Nav</Text> component to the active and disabled states. The base <Text el="strong">Nav</Text> component is built with flexbox and provides a strong foundation for building all types of navigation components.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Nav>
                        <Nav.Item>
                            <Nav.Link to="#" active>Active</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link to="#">Link</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link to="#">Link</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link to="#" disabled>Disabled</Nav.Link>
                        </Nav.Item>
                    </Nav>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {baseNavCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Horizontal Alignment */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Horizontal alignment
            </Heading>
            <Text className="mb-4">
                Change the horizontal alignment of your nav with flexbox utilities. By default, navs are left-aligned, but you can easily change them to center or right aligned.
            </Text>

            <Text el="p" className="mb-2 fw-medium">
                Centered with <Text el="code">.justify-content-center</Text>:
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Nav className="nav justify-content-center">
                        <Nav.Item>
                            <Nav.Link to="#" active>Active</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link to="#">Link</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link to="#">Link</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link to="#" disabled>Disabled</Nav.Link>
                        </Nav.Item>
                    </Nav>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {centeredNavCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Text el="p" className="mb-2 mt-4 fw-medium">
                Right-aligned with <Text el="code">.justify-content-end</Text>:
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Nav className="nav justify-content-end">
                        <Nav.Item>
                            <Nav.Link to="#" active>Active</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link to="#">Link</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link to="#">Link</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link to="#" disabled>Disabled</Nav.Link>
                        </Nav.Item>
                    </Nav>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {rightNavCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Vertical */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Vertical
            </Heading>
            <Text className="mb-4">
                Stack your navigation by changing the flex item direction with the <Text el="code">.flex-column</Text> utility. Need to stack them on some viewports but not others? Use the responsive versions (e.g., <Text el="code">.flex-sm-column</Text>).
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Nav className="nav flex-column">
                        <Nav.Item>
                            <Nav.Link to="#" active>Active</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link to="#">Link</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link to="#">Link</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link to="#" disabled>Disabled</Nav.Link>
                        </Nav.Item>
                    </Nav>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {verticalNavCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Tabs */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Tabs
            </Heading>
            <Text className="mb-4">
                Takes the basic nav from above and adds the <Text el="code">.nav-tabs</Text> class to generate a tabbed interface.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Nav className="nav nav-tabs">
                        <Nav.Item>
                            <Nav.Link to="#" active>Active</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link to="#">Link</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link to="#">Link</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link to="#" disabled>Disabled</Nav.Link>
                        </Nav.Item>
                    </Nav>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {tabsCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Pills */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Pills
            </Heading>
            <Text className="mb-4">
                Take that same HTML, but use <Text el="code">.nav-pills</Text> instead to create a pill-style navigation.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Nav className="nav nav-pills">
                        <Nav.Item>
                            <Nav.Link to="#" active>Active</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link to="#">Link</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link to="#">Link</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link to="#" disabled>Disabled</Nav.Link>
                        </Nav.Item>
                    </Nav>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {pillsCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Underline */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Underline
            </Heading>
            <Text className="mb-4">
                Take that same HTML, but use <Text el="code">.nav-underline</Text> instead to create an underline-style navigation.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Nav className="nav nav-underline">
                        <Nav.Item>
                            <Nav.Link to="#" active>Active</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link to="#">Link</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link to="#">Link</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link to="#" disabled>Disabled</Nav.Link>
                        </Nav.Item>
                    </Nav>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {underlineCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Fill and justify */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Fill and justify
            </Heading>
            <Text className="mb-4">
                Force your <Text el="code">.nav</Text>'s contents to extend the full available width with <Text el="code">.nav-fill</Text>. Notice that all horizontal space is occupied, but not every nav item has the same width.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Nav className="nav nav-pills nav-fill">
                        <Nav.Item>
                            <Nav.Link to="#" active>Active</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link to="#">Much longer nav link</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link to="#">Link</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link to="#" disabled>Disabled</Nav.Link>
                        </Nav.Item>
                    </Nav>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {fillCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Text className="mb-4 mt-4">
                For equal-width elements, use <Text el="code">.nav-justified</Text>. All horizontal space will be occupied by nav links, but unlike <Text el="code">.nav-fill</Text> above, every nav item will be the same width.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Nav className="nav nav-pills nav-justified">
                        <Nav.Item>
                            <Nav.Link to="#" active>Active</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link to="#">Much longer nav link</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link to="#">Link</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link to="#" disabled>Disabled</Nav.Link>
                        </Nav.Item>
                    </Nav>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {justifiedCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Working with flex utilities */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Working with flex utilities
            </Heading>
            <Text className="mb-4">
                If you need responsive nav variations, consider using a series of flexbox utilities. While more verbose, these utilities offer greater customization across responsive breakpoints.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Nav className="nav nav-pills flex-column flex-sm-row">
                        <Nav.Item>
                            <Nav.Link to="#" active className="flex-sm-fill text-sm-center">Active</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link to="#" className="flex-sm-fill text-sm-center">Longer nav link</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link to="#" className="flex-sm-fill text-sm-center">Link</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link to="#" disabled className="flex-sm-fill text-sm-center">Disabled</Nav.Link>
                        </Nav.Item>
                    </Nav>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {flexUtilitiesCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* JavaScript behavior - Tabs */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                JavaScript behavior - Tabs
            </Heading>
            <Text className="mb-4">
                Use the <Text el="strong">Tabs</Text> component to create tabbable panes of local content. The <Text el="code">eventKey</Text> prop on <Text el="code">Nav.Link</Text> connects each tab to its corresponding <Text el="code">Tabs.Pane</Text>. For controlled mode, pass <Text el="code">activeKey</Text> and <Text el="code">onSelect</Text> on <Text el="code">Tabs</Text> instead of (or in addition to) <Text el="code">defaultActiveKey</Text>.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Tabs defaultActiveKey="home" >
                        <Nav className="nav nav-tabs">
                            <Nav.Item>
                                <Nav.Link eventKey="home">Home</Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="profile">Profile</Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="contact">Contact</Nav.Link>
                            </Nav.Item>
                        </Nav>
                        <Tabs.Content className="mt-3">
                            <Tabs.Pane eventKey="home">
                                <Text>This is the <Text el="strong">Home</Text> tab content. It contains information about the home section.</Text>
                            </Tabs.Pane>
                            <Tabs.Pane eventKey="profile">
                                <Text>This is the <Text el="strong">Profile</Text> tab content. User profile information would go here.</Text>
                            </Tabs.Pane>
                            <Tabs.Pane eventKey="contact">
                                <Text>This is the <Text el="strong">Contact</Text> tab content. Contact details and forms would be displayed here.</Text>
                            </Tabs.Pane>
                        </Tabs.Content>
                    </Tabs>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {tabsExample}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Pills with content
            </Heading>
            <Text className="mb-4">
                Tabs also work with pills.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Tabs defaultActiveKey="home">
                        <Nav className="nav nav-pills">
                            <Nav.Item>
                                <Nav.Link eventKey="home">Home</Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="profile">Profile</Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="contact">Contact</Nav.Link>
                            </Nav.Item>
                        </Nav>
                        <Tabs.Content className="mt-3">
                            <Tabs.Pane eventKey="home">
                                <Text>Home content for pills navigation.</Text>
                            </Tabs.Pane>
                            <Tabs.Pane eventKey="profile">
                                <Text>Profile content for pills navigation.</Text>
                            </Tabs.Pane>
                            <Tabs.Pane eventKey="contact">
                                <Text>Contact content for pills navigation.</Text>
                            </Tabs.Pane>
                        </Tabs.Content>
                    </Tabs>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {pillsTabsExample}
                </CodeHighlighter>
            </CodeSnippet>

        </Container>
    );
}

export default NavPage;

// Code snippets
const baseNavCode = `<Nav className="nav">
    <Nav.Item>
        <Nav.Link to="#" active>Active</Nav.Link>
    </Nav.Item>
    <Nav.Item>
        <Nav.Link to="#">Link</Nav.Link>
    </Nav.Item>
    <Nav.Item>
        <Nav.Link to="#">Link</Nav.Link>
    </Nav.Item>
    <Nav.Item>
        <Nav.Link to="#" disabled>Disabled</Nav.Link>
    </Nav.Item>
</Nav>`;

const centeredNavCode = `<Nav className="nav justify-content-center">
    <Nav.Item>
        <Nav.Link to="#" active>Active</Nav.Link>
    </Nav.Item>
    <Nav.Item>
        <Nav.Link to="#">Link</Nav.Link>
    </Nav.Item>
    <Nav.Item>
        <Nav.Link to="#">Link</Nav.Link>
    </Nav.Item>
    <Nav.Item>
        <Nav.Link to="#" disabled>Disabled</Nav.Link>
    </Nav.Item>
</Nav>`;

const rightNavCode = `<Nav className="nav justify-content-end">
    <Nav.Item>
        <Nav.Link to="#" active>Active</Nav.Link>
    </Nav.Item>
    <Nav.Item>
        <Nav.Link to="#">Link</Nav.Link>
    </Nav.Item>
    <Nav.Item>
        <Nav.Link to="#">Link</Nav.Link>
    </Nav.Item>
    <Nav.Item>
        <Nav.Link to="#" disabled>Disabled</Nav.Link>
    </Nav.Item>
</Nav>`;

const verticalNavCode = `<Nav className="nav flex-column">
    <Nav.Item>
        <Nav.Link to="#" active>Active</Nav.Link>
    </Nav.Item>
    <Nav.Item>
        <Nav.Link to="#">Link</Nav.Link>
    </Nav.Item>
    <Nav.Item>
        <Nav.Link to="#">Link</Nav.Link>
    </Nav.Item>
    <Nav.Item>
        <Nav.Link to="#" disabled>Disabled</Nav.Link>
    </Nav.Item>
</Nav>`;

const tabsCode = `<Nav className="nav nav-tabs">
    <Nav.Item>
        <Nav.Link to="#" active>Active</Nav.Link>
    </Nav.Item>
    <Nav.Item>
        <Nav.Link to="#">Link</Nav.Link>
    </Nav.Item>
    <Nav.Item>
        <Nav.Link to="#">Link</Nav.Link>
    </Nav.Item>
    <Nav.Item>
        <Nav.Link to="#" disabled>Disabled</Nav.Link>
    </Nav.Item>
</Nav>`;

const pillsCode = `<Nav className="nav nav-pills">
    <Nav.Item>
        <Nav.Link to="#" active>Active</Nav.Link>
    </Nav.Item>
    <Nav.Item>
        <Nav.Link to="#">Link</Nav.Link>
    </Nav.Item>
    <Nav.Item>
        <Nav.Link to="#">Link</Nav.Link>
    </Nav.Item>
    <Nav.Item>
        <Nav.Link to="#" disabled>Disabled</Nav.Link>
    </Nav.Item>
</Nav>`;

const underlineCode = `<Nav className="nav nav-underline">
    <Nav.Item>
        <Nav.Link to="#" active>Active</Nav.Link>
    </Nav.Item>
    <Nav.Item>
        <Nav.Link to="#">Link</Nav.Link>
    </Nav.Item>
    <Nav.Item>
        <Nav.Link to="#">Link</Nav.Link>
    </Nav.Item>
    <Nav.Item>
        <Nav.Link to="#" disabled>Disabled</Nav.Link>
    </Nav.Item>
</Nav>`;

const fillCode = `<Nav className="nav nav-pills nav-fill">
    <Nav.Item>
        <Nav.Link to="#" active>Active</Nav.Link>
    </Nav.Item>
    <Nav.Item>
        <Nav.Link to="#">Much longer nav link</Nav.Link>
    </Nav.Item>
    <Nav.Item>
        <Nav.Link to="#">Link</Nav.Link>
    </Nav.Item>
    <Nav.Item>
        <Nav.Link to="#" disabled>Disabled</Nav.Link>
    </Nav.Item>
</Nav>`;

const justifiedCode = `<Nav className="nav nav-pills nav-justified">
    <Nav.Item>
        <Nav.Link to="#" active>Active</Nav.Link>
    </Nav.Item>
    <Nav.Item>
        <Nav.Link to="#">Much longer nav link</Nav.Link>
    </Nav.Item>
    <Nav.Item>
        <Nav.Link to="#">Link</Nav.Link>
    </Nav.Item>
    <Nav.Item>
        <Nav.Link to="#" disabled>Disabled</Nav.Link>
    </Nav.Item>
</Nav>`;

const flexUtilitiesCode = `<Nav className="nav nav-pills flex-column flex-sm-row">
    <Nav.Item>
        <Nav.Link to="#" active className="flex-sm-fill text-sm-center">Active</Nav.Link>
    </Nav.Item>
    <Nav.Item>
        <Nav.Link to="#" className="flex-sm-fill text-sm-center">Longer nav link</Nav.Link>
    </Nav.Item>
    <Nav.Item>
        <Nav.Link to="#" className="flex-sm-fill text-sm-center">Link</Nav.Link>
    </Nav.Item>
    <Nav.Item>
        <Nav.Link to="#" disabled className="flex-sm-fill text-sm-center">Disabled</Nav.Link>
    </Nav.Item>
</Nav>`;

const tabsExample = `<Tabs defaultActiveKey="home">
    <Nav className="nav nav-tabs">
        <Nav.Item>
            <Nav.Link eventKey="home">Home</Nav.Link>
        </Nav.Item>
        <Nav.Item>
            <Nav.Link eventKey="profile">Profile</Nav.Link>
        </Nav.Item>
        <Nav.Item>
            <Nav.Link eventKey="contact">Contact</Nav.Link>
        </Nav.Item>
    </Nav>
    <Tabs.Content className="mt-3">
        <Tabs.Pane eventKey="home">
            <Text>This is the Home tab content.</Text>
        </Tabs.Pane>
        <Tabs.Pane eventKey="profile">
            <Text>This is the Profile tab content.</Text>
        </Tabs.Pane>
        <Tabs.Pane eventKey="contact">
            <Text>This is the Contact tab content.</Text>
        </Tabs.Pane>
    </Tabs.Content>
</Tabs>`;

const pillsTabsExample = `<Tabs defaultActiveKey="home">
    <Nav className="nav nav-pills">
        <Nav.Item>
            <Nav.Link eventKey="home">Home</Nav.Link>
        </Nav.Item>
        <Nav.Item>
            <Nav.Link eventKey="profile">Profile</Nav.Link>
        </Nav.Item>
        <Nav.Item>
            <Nav.Link eventKey="contact">Contact</Nav.Link>
        </Nav.Item>
    </Nav>
    <Tabs.Content className="mt-3">
        <Tabs.Pane eventKey="home">
            <Text>Home content for pills navigation.</Text>
        </Tabs.Pane>
        <Tabs.Pane eventKey="profile">
            <Text>Profile content for pills navigation.</Text>
        </Tabs.Pane>
        <Tabs.Pane eventKey="contact">
            <Text>Contact content for pills navigation.</Text>
        </Tabs.Pane>
    </Tabs.Content>
</Tabs>`;
